import type { JSX } from 'react'
/**
 * Route-level authorization.
 *
 * Hiding a button is not enough: a user typing /crm/leads directly must be
 * bounced. This is still cosmetic — the API refuses the request regardless —
 * but it is the difference between a clear message and a screen of failed
 * loaders.
 */

import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth, useCan } from './auth-context'

export function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const { me, loading } = useAuth()
  const location = useLocation()

  if (loading) return <Loading />
  if (me === null) {
    const returnTo = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />
  }

  return <>{children}</>
}

export function RequirePermission({
  any,
  children,
}: {
  any: string[]
  children: ReactNode
}): JSX.Element {
  const { loading } = useAuth()
  const can = useCan()

  if (loading) return <Loading />
  if (!any.some(can)) return <NoAccess needed={any} />

  return <>{children}</>
}

function Loading(): JSX.Element {
  return (
    <div className="erp-centre">
      <p className="erp-muted">Loading…</p>
    </div>
  )
}

function NoAccess({ needed }: { needed: string[] }): JSX.Element {
  return (
    <div className="erp-centre">
      <h2>No access</h2>
      <p className="erp-muted">
        This screen needs one of: <code>{needed.join('</code>, <code>')}</code>
      </p>
      <p className="erp-muted">Ask an administrator to grant it.</p>
    </div>
  )
}